import type { GlobalPageCard } from '@confluence/page-card/entry-points/cardTypes.ts';
import { transformGlobalContent } from '@confluence/page-card/entry-points/transformers.ts';

import type { EndOfPageRecommendationQuery_getRecommendedPages_recommendedPages as EndOfPageRecommendationData } from './__types__/EndOfPageRecommendationQuery.ts';

export const transformEndOfPageRecContent = (
	data: EndOfPageRecommendationData,
	strategiesCounter: any,
): GlobalPageCard => {
	const pageCardData: GlobalPageCard = transformGlobalContent(data.content);

	pageCardData.likesCount = 0;
	pageCardData.commentsCount = 0;
	pageCardData.recommendationStrategy = chooseStrategy(data, strategiesCounter);
	return pageCardData;
};

/**
 * @param strategiesCounter is a hashmap that keeps track of how many times each strategy has been used
 * so that we can select the lowest occuring strategy on the next data point. This helps to reduce
 * monotony of strategies on the UI
 * @returns the selected strategy for each rec which will be applied to @function transformEndOfPageRecContent
 */
export const chooseStrategy = (data: any, strategiesCounter: any): any => {
	const strategies = data.strategy;

	if (strategies.includes('CONTENT_TO_CONTENT')) {
		return 'CONTENT_TO_CONTENT';
	}

	const currentCounter = new Map();
	strategies.forEach((entry) => {
		const entryCount = strategiesCounter.get(entry) || 0;
		currentCounter.set(entry, entryCount);
	});
	const sortedCounter = new Map([...currentCounter.entries()].sort((a, b) => a[1] - b[1]));
	const strategy = [...sortedCounter.keys()][0];
	const count = strategiesCounter.get(strategy) || 0;
	strategiesCounter.set(strategy, count + 1);
	return strategy;
};

/**
 * The ordering of recs array is determined by the "ranking" in the backend.
 * Each rec will have a corresponding "strategies" array that can have one or multiple strategies.
 * We want to ensure that we are choosing strategies from each rec in a way that avoids monotony on the UI.
 * To accomplish this, we must first sort the recs array depending on the number of strategies each rec has, in
 * ascending order. We then pass each rec to transformEndOfPageRecContent which helps to choose the strategy, and
 * finally we return the transformed array back to the intial ranking order determind by the backend
 */
export const transformAndOrderRecs = (
	recs: EndOfPageRecommendationData[] | undefined,
): GlobalPageCard[] | undefined => {
	if (!recs) return;

	const strategiesCounter = new Map();
	const recsSortedByStrategyCount = [...recs].sort((a, b) =>
		a.strategy.length > b.strategy.length ? 1 : -1,
	);
	const transformedRecs = recsSortedByStrategyCount.map((rec) =>
		transformEndOfPageRecContent(rec, strategiesCounter),
	);

	// return to initial order
	const pageCards: GlobalPageCard[] = new Array();
	recs.forEach((rec: EndOfPageRecommendationData) => {
		const contentId = rec?.content?.id;
		if (!contentId) {
			return;
		}
		const el = transformedRecs.find((card) => card?.id === contentId);
		if (el) {
			pageCards.push(el);
		}
	});
	return pageCards;
};
